import { useMemo } from 'react';
import { extractHashtags, processUsernames } from '../lib/utils';
import { formatContent, parseHtmlEntities, formatRedditSubredditDisplay } from '../lib/postUtils';
import { MediaRenderer, getMediaInfo } from './MediaRenderer';
import { HashtagDisplay } from './ui/hashtag-display';
import { LinkedInArticleComponent } from './LinkedInArticleComponent';
import { LinkedInPollComponent } from './LinkedInPollComponent';
import { useImageProxy } from '../hooks/useImageProxy';

// Component for profile image with proxy support (proxy API is Instagram-only)
const ProfileImage = ({ url, alt, postId, platform, className }: { url: string | null | undefined; alt: string; postId: number | null; platform?: string | null; className?: string }) => {
  const proxiedUrl = useImageProxy(url, postId, 'profile_picture', platform);
  return (
    <img
      src={proxiedUrl ?? url ?? "/assets/placeholder.jpg"}
      alt={alt}
      className={className}
      onError={(e) => {
        e.currentTarget.src = "/assets/placeholder.jpg";
      }}
    />
  );
};

// Helper function to truncate text to word limit
const truncateToWords = (text: string, wordLimit: number = 200): string => {
  if (!text) return '';
  
  const words = text.trim().split(/\s+/);
  
  if (words.length <= wordLimit) {
    return text;
  }
  
  const truncated = words.slice(0, wordLimit).join(' ');
  return truncated + '...';
};

// ============================================================================
// NORMALIZATION HELPERS
// These functions handle both cases:
// 1. Full API response with nested platform objects (instagram_post, x_post, etc.)
// 2. Direct platform-specific object passed
//
// API Response Structure (from API_RESPONSE_EXAMPLES.md):
// - Instagram author: { username, full_name, profile_pic_url, is_verified }
// - X (Twitter) author: { user: { name, username, profile_image, verified, description } }
// - LinkedIn author: { name, profile_url, profile_image, headline }
// - TikTok author: { unique_id, nickname, avatar_url, verified }
// ============================================================================

interface NormalizedAuthor {
  name: string;
  handle: string;
  avatar: string;
  headline?: string;
  verified?: boolean;
}

/**
 * Extracts the platform-specific post object from either:
 * - Full API response: { instagram_post: {...}, x_post: null, ... }
 * - Direct object: The platform object itself
 */
const getPlatformPost = (post: any, platform: 'INSTAGRAM' | 'X' | 'LINKEDIN' | 'TIKTOK' | 'REDDIT' | 'CHROME_BOOKMARK'): any => {
  if (!post) return null;
  
  switch (platform) {
    case 'INSTAGRAM':
      return post.instagram_post || post;
    case 'X':
      return post.x_post || post;
    case 'LINKEDIN':
      return post.linkedin_post || post;
    case 'TIKTOK':
      return post.tiktok_post || post;
    case 'REDDIT':
      return post.reddit_post || post;
    case 'CHROME_BOOKMARK':
      return post.chrome_bookmark_post || post;
    default:
      return post;
  }
};

/**
 * Normalizes author information from different platform structures
 * Handles both cases:
 * 1. Author passed as prop
 * 2. Author extracted from platform post object
 */
const normalizeAuthor = (
  authorFromProps: any,
  platformPost: any,
  platform: 'INSTAGRAM' | 'X' | 'LINKEDIN' | 'TIKTOK' | 'REDDIT' | 'CHROME_BOOKMARK'
): NormalizedAuthor => {
  const defaultAuthor: NormalizedAuthor = {
    name: 'Unknown User',
    handle: '',
    avatar: '/assets/placeholder.jpg'
  };

  // First try author from props, then from platform post
  const author = authorFromProps || platformPost?.author;

  // Reddit can show subreddit as title with no author object
  if (!author) {
    const redditSub = platform === 'REDDIT' ? formatRedditSubredditDisplay(platformPost?.subreddit) : null;
    if (!redditSub) return defaultAuthor;
  }

  switch (platform) {
    case 'INSTAGRAM': {
      // Instagram author: { username, full_name, profile_pic_url, is_verified } (API examples doc)
      // or { name, username, profile_picture } (types file)
      return {
        name: author.full_name || author.name || author.username || defaultAuthor.name,
        handle: author.username || '',
        avatar: author.profile_pic_url || author.profile_picture || author.avatar || defaultAuthor.avatar,
        verified: author.is_verified
      };
    }
    
    case 'X': {
      // X author has nested user: { user: { name, username, profile_image, verified } }
      const user = author.user || author;
      return {
        name: user.name || user.username || defaultAuthor.name,
        handle: user.username || user.screen_name || '',
        avatar: user.profile_image || user.profile_image_url_https || user.profile_image_url || defaultAuthor.avatar,
        verified: user.verified
      };
    }
    
    case 'LINKEDIN': {
      // LinkedIn author: { full_name, profile_image, headline } (actual API)
      // or { name, profile_url, profile_image, headline } (API examples doc)
      return {
        name: author.full_name || author.name || defaultAuthor.name,
        handle: '', // LinkedIn doesn't have username handles
        avatar: author.profile_image || author.avatar || defaultAuthor.avatar,
        headline: author.headline
      };
    }
    
    case 'TIKTOK': {
      // TikTok author: { unique_id, nickname, avatar_url, verified } (API examples doc)
      // or { username, name, profile_picture } (types file)
      return {
        name: author.nickname || author.name || author.unique_id || author.username || defaultAuthor.name,
        handle: author.unique_id || author.username || '',
        avatar: author.avatar_url || author.profile_picture || author.avatar || defaultAuthor.avatar,
        verified: author.verified
      };
    }

    case 'REDDIT': {
      const displaySub = formatRedditSubredditDisplay(platformPost?.subreddit);
      const user = authorFromProps || platformPost?.author;
      if (!user && !displaySub) return defaultAuthor;
      return {
        name: displaySub || user?.nickname || user?.name || user?.unique_id || user?.username || defaultAuthor.name,
        handle: displaySub && user?.username ? `@${user.username}` : (user?.unique_id || user?.username || ''),
        avatar: user?.avatar_url || user?.profile_picture || user?.avatar || defaultAuthor.avatar,
        verified: user?.verified
      };
    }

    case 'CHROME_BOOKMARK': {
      if (authorFromProps) return authorFromProps;
      return defaultAuthor;
    }

    default:
      return defaultAuthor;
  }
};

/**
 * Extracts content from platform-specific post structures
 * API content fields per platform:
 * - Instagram: caption
 * - X (Twitter): content
 * - LinkedIn: content
 * - TikTok: content
 */
const extractContent = (
  post: any,
  platformPost: any,
  platform: 'INSTAGRAM' | 'X' | 'LINKEDIN' | 'TIKTOK' | 'REDDIT' | 'CHROME_BOOKMARK'
): string => {
  switch (platform) {
    case 'INSTAGRAM':
      // Instagram uses 'caption' field
      return platformPost?.caption || post?.caption || post?.content || '';
      
    case 'X':
      // X (Twitter) uses 'content' field
      return platformPost?.content || post?.content || post?.text || '';
      
    case 'LINKEDIN':
      // LinkedIn uses 'content' field
      return platformPost?.content || post?.content || post?.text || '';
      
    case 'TIKTOK':
      // TikTok uses 'content' field
      return platformPost?.content || post?.content || '';

    case 'REDDIT':
      return platformPost?.content || post?.content || '';

    case 'CHROME_BOOKMARK':
      return platformPost?.og_description || '';

    default:
      return post?.content || '';
  }
};

/**
 * Extracts quoted content based on platform
 * - X (Twitter): quoted_tweet
 * - LinkedIn: quoted_post
 */
const extractQuotedContent = (
  post: any,
  platformPost: any,
  platform: 'INSTAGRAM' | 'X' | 'LINKEDIN' | 'TIKTOK' | 'REDDIT' | 'CHROME_BOOKMARK'
): { quotedTweet: any; quotedPost: any } => {
  let quotedTweet = null;
  let quotedPost = null;
  
  if (platform === 'X') {
    quotedTweet = platformPost?.quoted_tweet || post?.quoted_tweet;
  } else if (platform === 'LINKEDIN') {
    quotedPost = platformPost?.quoted_post || post?.quoted_post;
  }
  
  return { quotedTweet, quotedPost };
};

/**
 * Extracts LinkedIn-specific components
 * - article_component: For shared articles/links
 * - poll_component: For polls
 */
const extractLinkedInComponents = (
  post: any,
  platformPost: any
): { articleComponent: any; pollComponent: any } => {
  const articleComponent = platformPost?.article_component || post?.article_component || null;
  const pollComponent = platformPost?.poll_component || post?.poll_component || null;
  
  return { articleComponent, pollComponent };
};

// Helper component to render text with clickable usernames
const ProcessedText = ({ text, platform }: { text: string, platform: 'INSTAGRAM' | 'X' | 'LINKEDIN' | 'TIKTOK' | 'REDDIT' | 'CHROME_BOOKMARK' }) => {
  const processedParts = processUsernames(text, platform);

  return (
    <>
      {processedParts.map((part, index) => {
        if (part.type === 'username') {
          return (
            <a
              key={`username-${index}`}
              href={part.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-400 transition-colors cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              {part.content}
            </a>
          );
        } else {
          return part.content;
        }
      })}
    </>
  );
};

// Component for displaying quoted tweets (X/Twitter)
const QuotedTweet = ({ quotedTweet }: { quotedTweet: any }) => {
  const formatQuotedContent = (content: string) => {
    if (!content) return '';
    const parsedContent = parseHtmlEntities(content);
    const lines = parsedContent.split('\n');
    return lines.join('\n');
  };

  const quotedMediaInfo = useMemo(() => getMediaInfo(quotedTweet), [quotedTweet]);

  // X quoted tweet author: { user: { name, username, profile_image } }
  const author = quotedTweet?.author;
  const user = author?.user || author;
  const userName = user?.name || user?.username || "Unknown User";
  const userUsername = user?.username || "unknown";
  const userProfileImage = user?.profile_image || user?.profile_image_url_https || "/assets/placeholder.jpg";

  // Truncate quoted tweet content to 100 words
  const formattedContent = formatQuotedContent(quotedTweet.content);

  return (
    <div className="border border-gray-600 rounded-lg p-3 my-3 bg-gray-800/50">
      <div className="flex items-start space-x-2 mb-2">
        <img
          src={userProfileImage}
          alt={userName}
          className="w-10 h-10 rounded-full"
          onError={(e) => {
            e.currentTarget.src = "/assets/placeholder.jpg";
          }}
        />
        <div>
          <div className="flex flex-col space-y-1">
            <span className="font-medium text-white text-sm">{userName}</span>
            <span className="text-gray-400 text-sm">@{userUsername}</span>
          </div>
        </div>
      </div>

      <div className="mb-2">
        <p className="text-white text-sm leading-relaxed whitespace-pre-line">
          {formattedContent}
        </p>
      </div>

      {(() => {
        const hasMedia = Array.isArray(quotedMediaInfo) ? quotedMediaInfo.length > 0 : !!quotedMediaInfo;
        return hasMedia;
      })() && (
        <div className="mb-2">
          <MediaRenderer
            mediaInfo={quotedMediaInfo}
            maxHeight="200px"
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};

// Component for displaying quoted LinkedIn posts
const QuotedLinkedInPost = ({ quotedPost }: { quotedPost: any }) => {
  const formatQuotedContent = (content: string) => {
    if (!content) return '';
    const parsedContent = parseHtmlEntities(content);
    const lines = parsedContent.split('\n');
    return lines.join('\n');
  };

  const quotedMediaInfo = useMemo(() => getMediaInfo(quotedPost), [quotedPost]);

  // LinkedIn quoted post author: { name, profile_image }
  const author = quotedPost?.author;
  const userName = author?.name || author?.full_name || "Unknown User";
  const userProfileImage = author?.profile_image || author?.avatar || "/assets/placeholder.jpg";

  // LinkedIn quoted post uses 'text' or 'content' field
  const rawContent = quotedPost?.text || quotedPost?.content || '';
  const formattedContent = formatQuotedContent(rawContent);

  return (
    <div className="border border-gray-600 rounded-lg p-3 my-3 bg-gray-800/50">
      <div className="flex items-start space-x-2 mb-2">
        <img
          src={userProfileImage}
          alt={userName}
          className="w-10 h-10 rounded-full"
          onError={(e) => {
            e.currentTarget.src = "/assets/placeholder.jpg";
          }}
        />
        <div>
          <div className="flex flex-col space-y-1">
            <span className="font-medium text-white text-sm">{userName}</span>
            <span className="text-gray-400 text-sm">LinkedIn</span>
          </div>
        </div>
      </div>

      <div className="mb-2">
        <div className="text-white text-sm leading-relaxed whitespace-pre-line">
          <ProcessedText text={formattedContent} platform="LINKEDIN" />
        </div>
      </div>

      {(() => {
        const hasMedia = Array.isArray(quotedMediaInfo) ? quotedMediaInfo.length > 0 : !!quotedMediaInfo;
        return hasMedia;
      })() && (
        <div className="mb-2">
          <MediaRenderer
            mediaInfo={quotedMediaInfo}
            maxHeight="200px"
            className="w-full"
          />
        </div>
      )}
    </div>
  );
};

interface FullPostPopupProps {
  post: any;
  author: any;
  platform: 'INSTAGRAM' | 'X' | 'LINKEDIN' | 'TIKTOK' | 'REDDIT' | 'CHROME_BOOKMARK';
  onClose: () => void;
  postId?: number; // Optional post ID for image proxying
}

export const FullPostPopup = ({ post, author: authorProp, platform, onClose, postId }: FullPostPopupProps) => {
  // Get the platform-specific post object
  const platformPost = useMemo(() => getPlatformPost(post, platform), [post, platform]);
  
  // Extract post ID from post object if not provided
  const resolvedPostId = postId || platformPost?.post_id || post?.post_id || post?.id || null;
  
  // Normalize author information
  const normalizedAuthor = useMemo(
    () => normalizeAuthor(authorProp, platformPost, platform),
    [authorProp, platformPost, platform]
  );
  
  // Extract content based on platform
  const rawContent = useMemo(
    () => extractContent(post, platformPost, platform),
    [post, platformPost, platform]
  );
  
  // Extract quoted content
  const { quotedTweet, quotedPost } = useMemo(
    () => extractQuotedContent(post, platformPost, platform),
    [post, platformPost, platform]
  );
  
  // Extract LinkedIn-specific components (article, poll)
  const { articleComponent, pollComponent } = useMemo(
    () => extractLinkedInComponents(post, platformPost),
    [post, platformPost]
  );

  const content = formatContent(rawContent);
  const { hashtags, cleanedText } = extractHashtags(content);
  
  // Get media info
  const mediaInfo = useMemo(() => getMediaInfo(post), [post]);

  // Determine if media exists
  const hasMedia = Array.isArray(mediaInfo) ? mediaInfo.length > 0 : !!mediaInfo;

  return (
    <div className="fixed inset-0 flex items-stretch z-50">
      {/* Left semi-transparent area (25%) */}
      <div className="w-1/4 bg-black/85" />

      {/* Center content area (50%) */}
      <div className="w-1/2 bg-slate-800 min-h-screen overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-slate-800 z-10">
          <h2 className="text-xl font-semibold text-white">Post by {normalizedAuthor.name}</h2>
          <button onClick={onClose} className="text-gray-400 bg-transparent hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-8">
            <div className="flex items-start space-x-3 mb-4">
              <ProfileImage
                url={normalizedAuthor.avatar}
                alt={normalizedAuthor.name}
                postId={resolvedPostId}
                platform={platform}
                className="w-12 h-12 rounded-full"
              />
              <div>
                <div className="font-semibold text-white text-lg">{normalizedAuthor.name}</div>
                {normalizedAuthor.handle && (
                  <div className="text-sm text-gray-400">@{normalizedAuthor.handle}</div>
                )}
                {/* LinkedIn-specific: show headline if available */}
                {platform === 'LINKEDIN' && normalizedAuthor.headline && (
                  <div className="text-sm text-gray-400">{normalizedAuthor.headline}</div>
                )}
              </div>
            </div>

            <div className="mb-4">
              {platform === 'REDDIT' && platformPost?.title ? (
                <div className="font-bold text-white text-base mb-2 leading-snug">
                  {platformPost.title}
                </div>
              ) : null}
              {platform === 'CHROME_BOOKMARK' ? (
                <div className="rounded-xl overflow-hidden border border-gray-600 bg-gray-800/60">
                  {platformPost?.og_image_url && (
                    <img
                      src={platformPost.og_image_url}
                      alt={platformPost.og_title || platformPost.title || ''}
                      className="w-full max-h-64 object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <div className="p-4">
                    {(platformPost?.og_title || platformPost?.title) && (
                      <div className="font-semibold text-white text-base mb-1 leading-snug">
                        {platformPost.og_title || platformPost.title}
                      </div>
                    )}
                    {platformPost?.og_description && (
                      <div className="text-gray-300 text-sm leading-relaxed mb-3">
                        {platformPost.og_description}
                      </div>
                    )}
                    {platformPost?.url && (
                      <a
                        href={platformPost.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-400 hover:text-purple-300 text-sm break-all"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {platformPost.url}
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-white text-base leading-relaxed whitespace-pre-line">
                  <ProcessedText text={cleanedText} platform={platform} />
                </div>
              )}
            </div>

            {/* Hashtags */}
            {hashtags.length > 0 && (
              <div className="mb-4">
                <HashtagDisplay hashtags={hashtags} />
              </div>
            )}

            {/* Media */}
            {hasMedia && (
              <div className="mt-4">
                <div className="relative min-h-[200px] max-h-[500px] w-full">
                  <MediaRenderer
                    mediaInfo={mediaInfo}
                    maxHeight="500px"
                    className="w-full"
                    style={{
                      aspectRatio: 'auto',
                      minHeight: '200px',
                      maxHeight: '500px'
                    }}
                    post={post}
                  />
                </div>
              </div>
            )}

            {/* LinkedIn Article Component */}
            {platform === 'LINKEDIN' && articleComponent && (
              <LinkedInArticleComponent articleComponent={articleComponent} />
            )}

            {/* LinkedIn Poll Component */}
            {platform === 'LINKEDIN' && pollComponent && (
              <LinkedInPollComponent pollComponent={pollComponent} />
            )}

            {/* Quoted Tweet - only for X posts */}
            {platform === 'X' && quotedTweet && (
              <QuotedTweet quotedTweet={quotedTweet} />
            )}

            {/* Quoted Post - for LinkedIn posts */}
            {platform === 'LINKEDIN' && quotedPost && (
              <QuotedLinkedInPost quotedPost={quotedPost} />
            )}
          </div>
        </div>
      </div>

      {/* Right semi-transparent area (25%) */}
      <div className="w-1/4 bg-black/85" />
    </div>
  );
};
